import { z } from "zod/mini";
import { BigNumber } from "bignumber.js";
import {
    add,
    addBusinessDays as addBusinessDaysOriginal,
    addDays,
    addWeeks,
    isBefore,
    isEqual,
    lastDayOfISOWeek,
    parse as parseDate,
    parseISO,
    subDays,
    type Duration,
} from "date-fns";
import { format, toDate, type FormatOptionsWithTZ } from "date-fns-tz";
import type { IsNever } from "ts-essentials";
import { getDomain } from "tldts";
import { today, getLocalTimeZone, fromDate, CalendarDate, parseDate as parseDate2 } from "@internationalized/date";

export {
    type DateValue,
    CalendarDate,
    DateFormatter,
    getLocalTimeZone,
    parseDate as parseDate2,
    toCalendarDate,
    startOfWeek,
    endOfWeek,
} from "@internationalized/date";

export { evaluateCondition } from "./evaluateCondition.js";
export { render, renderJson, renderedObject } from "./render.js";
export { PrefixTree } from "./prefixTree.js";

export function todayLocal() {
    return today(getLocalTimeZone());
}

export function todayLocalISO() {
    return todayLocal().toString();
}

export function fromDateLocal(d: Date) {
    return fromDate(d, getLocalTimeZone());
}

export function isoDateString(v: string | null): string | null {
    if (!v || v.length < 10) {
        return null;
    }
    return tryParseDate(v.slice(0, 10))?.toString() ?? null;
}

export function tryParseDate(v: string | null) {
    if (!v) {
        return null;
    }

    try {
        return parseDate2(v);
    } catch (_) {
        return null;
    }
}

export {
    subSeconds,
    subDays,
    formatISO,
    parseISO,
    differenceInHours,
    differenceInDays,
    parse as parseDate,
    isValid as isValidDate,
    add as addToDate,
    sub as subFromDate,
    differenceInBusinessDays,
    min as minDate,
    max as maxDate,
    getDay,
    lastDayOfISOWeek,
    lastDayOfMonth,
    isBefore,
    isAfter,
    getISOWeek,
} from "date-fns";

export {
    getDomain,
    getDomainWithoutSuffix,
    getHostname,
    getPublicSuffix,
    getSubdomain,
    parse as parseDomain,
} from "tldts";

export function getDomainExcludingPopulars(input: string, additionalExcludes?: string[]) {
    const domain = getDomain(input)?.toLowerCase().trim();
    if (!domain) {
        return null;
    }

    const excluded = [
        "googlemail.",
        "gmail.",
        "hotmail.",
        "yahoo.",
        "hotmail.",
        "outlook.",
        "yahoo.",
        "aol.",
        "btinternet.",
        "btconnect.",
        "yahoo.",
        "web.de",
        "t-online.",
        ...(additionalExcludes ?? []),
    ];

    if (excluded.some(x => domain.includes(x.toLowerCase().trim()))) {
        return null;
    }

    return domain;
}

export function formatIsoDate(date: Date | string | number, options?: FormatOptionsWithTZ) {
    return formatDate(date, "yyyy-MM-dd", options);
}

z.config(z.core.locales.en());

export function formatDate(date: Date | string | number, formatStr?: string, options?: FormatOptionsWithTZ) {
    date = date instanceof Date ? date : toDate(date);
    return format(date, formatStr ?? "dd.MM.yyyy", options ?? { timeZone: "UTC" });
}

/**
 * Parses a date string, assuming UTC when no timezone offset is present.
 * If the string already contains a timezone indicator (Z, +HH:MM, etc.), it is respected as-is.
 */
export function parseDateString(value: string) {
    const normalized = /[Zz]$|[+-]\d{2}(:?\d{2})?$/.test(value) ? value : value + "Z";
    return parseISO(normalized);
}

export function formatLocalizedDate(
    date: Date | string | number,
    locale: string,
    options: { withTime?: boolean } = {},
) {
    const parsed = date instanceof Date ? date : typeof date === "string" ? parseDateString(date) : toDate(date);
    return new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        ...(options.withTime
            ? {
                  hour: "numeric",
                  minute: "numeric",
                  second: "numeric",
              }
            : {}),
    }).format(parsed);
}

export { z };
export { BigNumber };

export const BigNumberSchema = z
    .pipe(
        z.union([
            z.string(),
            z.number(),
            z.custom<BigNumber>(x => BigNumber.isBigNumber(x)).register(z.globalRegistry, { title: "BigNumber" }),
        ]),
        z.transform(x => BigNumber(x)),
    )
    .register(z.globalRegistry, { title: "BigNumberValue" });

export const PathSchema = z.array(z.union([z.string(), z.number()]));
export type Path = z.infer<typeof PathSchema>;

export function set(obj: Record<string | number, unknown>, path: Path, value: unknown) {
    if (path.length === 0) {
        throw new Error("path is empty");
    }

    const lastIndex = path.length - 1;
    let nested = obj;
    for (let i = 0; i < path.length; i++) {
        const key = path[i] as string | number;
        if (i === lastIndex) {
            nested[key] = value;
        } else {
            const current = nested[key];
            if (current && typeof current === "object") {
                nested = current as Record<string | number, unknown>;
            } else {
                const x = {};
                nested[key] = x;
                nested = x;
            }
        }
    }
}

export function get(x: Serializable, path: Path) {
    if (path.length === 0) {
        return x;
    }

    let nested = x;
    for (const k of path) {
        if (
            nested === null ||
            typeof nested === "string" ||
            typeof nested === "boolean" ||
            typeof nested === "number" ||
            !(k in nested)
        ) {
            return undefined;
        }

        nested = nested[k as never];
    }
    return nested;
}

export function isPromiseLike<T>(x: unknown): x is Promise<T> {
    return x !== null && typeof x === "object" && "then" in x && typeof x.then === "function";
}

export function* walk(x: Serializable) {
    const queue = [
        {
            key: "" as string | number,
            value: x,
            path: "",
            parentPaths: [] as string[],
            parent: null as null | SerializableObject | Serializable[],
        },
    ];
    while (queue.length > 0) {
        const { key, value, path, parentPaths, parent } = definedOrDie(queue.shift());
        const leaf =
            value === null ||
            typeof value === "string" ||
            typeof value === "boolean" ||
            typeof value === "number" ||
            BigNumber.isBigNumber(value);
        yield { key, value, path, parentPaths, root: path === "", leaf, parent };
        if (leaf) {
            continue;
        }
        if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
                queue.push({
                    key: i,
                    value: indexOrDie(value, i),
                    path: path === "" ? String(i) : `${path}.${i}`,
                    parentPaths: [...parentPaths, path],
                    parent: value,
                });
            }
        } else {
            for (const [k, v] of Object.entries(value)) {
                queue.push({
                    key: k,
                    value: v,
                    path: path === "" ? k : `${path}.${k}`,
                    parentPaths: [...parentPaths, path],
                    parent: value,
                });
            }
        }
    }
}

type IsNull<T> = IsNever<T> extends true ? false : [T] extends [null] ? ([null] extends [T] ? true : false) : false;
type IsUndefined<T> =
    IsNever<T> extends true ? false : [T] extends [undefined] ? ([undefined] extends [T] ? true : false) : false;
type Undefinable<T> = IsUndefined<T> extends true ? never : undefined extends T ? T : never;
type Nullable<T> = IsNull<T> extends true ? never : null extends T ? T : never;

export function definedOrDie<T>(arg: Undefinable<T>, message?: string): Exclude<T, undefined> {
    if (arg === undefined) {
        throw new Error(message ?? "Argument was undefined");
    }
    return arg as Exclude<T, undefined>;
}

export function notNullOrDie<T>(arg: Nullable<T>, message?: string): Exclude<T, null> {
    if (arg === null) {
        throw new Error(message ?? "Argument was null");
    }
    return arg as Exclude<T, null>;
}

export function definedNotNullOrDie<T>(arg: Nullable<Undefinable<T>>, message?: string): Exclude<T, null | undefined> {
    if (arg === null) {
        throw new Error(message ?? "Argument was null");
    }
    if (arg === undefined) {
        throw new Error(message ?? "Argument was undefined");
    }
    return arg as Exclude<T, null | undefined>;
}

export function isNotNull<T>(arg: Nullable<T>): arg is Exclude<Nullable<T>, null> {
    return arg !== null;
}

export function isDefined<T>(arg: Undefinable<T>): arg is Exclude<Undefinable<T>, undefined> {
    return arg !== undefined;
}

export function isDefinedNotNull<T>(
    arg: Nullable<Undefinable<T>>,
): arg is Exclude<Nullable<Undefinable<T>>, undefined | null> {
    return isNotNull(arg as unknown) && isDefined(arg as unknown);
}

export function indexOrDie<T>(array: ArrayLike<T>, index: number): T {
    if (index < 0 || index >= array.length) {
        throw new Error(`Array index ${String(index)} not found in Array[${String(array.length)}]`);
    }
    return array[index]!;
}

export function keyOrDie<K, T>(arg: Map<K, T>, key: K, message?: string): Exclude<T, undefined>;
export function keyOrDie<T extends object, K extends keyof T>(obj: T, key: K, message?: string): T[K];
export function keyOrDie<T>(
    arg: Record<string | number | symbol, T>,
    key: string | number,
    message?: string,
): Exclude<T, undefined>;
export function keyOrDie(
    arg: Map<unknown, unknown> | Record<string | number | symbol, unknown>,
    key: unknown,
    message?: string,
) {
    const value = arg instanceof Map ? arg.get(key) : arg[key as string];
    if (value === undefined) {
        throw new Error(
            message ??
                `Key ${String(key)} not found. Keys: ${JSON.stringify(arg instanceof Map ? Array.from(arg.keys()) : Object.keys(arg))}`,
        );
    }

    return value;
}

export function firstOrDie<T>(array: ArrayLike<T>) {
    return indexOrDie(array, 0);
}

export function singleOrDie<T>(array: ArrayLike<T>, message?: string) {
    if (array.length !== 1) {
        throw new Error(
            message ?? `Expected the array to have only one element but it contains ${String(array.length)}`,
        );
    }
    return indexOrDie(array, 0);
}

export function lastOrDie<T>(array: ArrayLike<T>) {
    return indexOrDie(array, array.length - 1);
}

export function stringOrDie(value: unknown, message?: string): string {
    if (typeof value !== "string") {
        throw new Error(message ?? `Expected string but got ${typeof value}: ${String(value)}`);
    }
    return value;
}

export function last<T>(array: ArrayLike<T>) {
    return array[array.length - 1];
}

export function mustNeverExist(target: never, message?: string): Error {
    let resultMessage = `Must never exist: ${serialize(target)}`;
    if (message) {
        resultMessage = `${message}\n${resultMessage}`;
    }
    return new Error(resultMessage);
}

export { customAlphabet, nanoid } from "nanoid";

export function equalsIgnoreCase(a: string, b: string) {
    return a.localeCompare(b, undefined, { sensitivity: "accent" }) === 0;
}

export function equalsIgnoreCaseTrimmed(a: string, b: string) {
    return a.trim().localeCompare(b.trim(), undefined, { sensitivity: "accent" }) === 0;
}

export {
    type Primitive,
    type DeepRequired,
    type MarkOptional,
    type Head,
    type Tail,
    type IsNever,
    type DeepReadonly,
    type MergeN,
    type Prettify,
    type Exact,
    type MarkRequired,
    type AsyncOrSync,
    type NonNever,
    type Merge,
    type IsUnknown,
    type UnionToIntersection,
    type AnyNonEmptyArray,
    type DeepPartial,
    type DeepOmit,
    assert,
    type AnyFunction,
    type OmitProperties,
    type DeepWritable,
    type OmitNeverProperties,
} from "ts-essentials";

export type DeepOmitProperties<Type, Value> = {
    [Key in keyof Type as Type[Key] extends Value ? never : Key]: DeepOmitProperties<Type[Key], Value>;
};

export type ErrorMessage<T extends string> = T & Error;

export function unreachable(message: string, x: never): never {
    throw new Error(`Unreachable: ${message} ${String(x)}`);
}

export function notSupported(message: string): never {
    throw new Error(`Not supported: ${message}`);
}

export function trimStart(s: string, c: string) {
    let i = 0;
    for (; i < s.length; i++) {
        if (s[i] !== c) {
            break;
        }
    }
    return s.slice(i);
}

export function chunkBy<T>(arr: T[], chunkSize: number) {
    if (chunkSize <= 0) {
        throw new Error("chunkSize must be a positive integer");
    }
    const chunks: T[][] = [];
    const count = Math.ceil(arr.length / chunkSize);
    for (let i = 0; i < count; i++) {
        const start = i * chunkSize;
        chunks.push(arr.slice(start, start + chunkSize));
    }
    return chunks;
}

export function keyBy<T, K extends string | number | symbol, R = T>(
    arr: readonly T[],
    key: (x: T, i: number) => K,
    value: (x: T, i: number) => R = (x: T) => x as unknown as R,
): Record<K, R> {
    return arr.reduce(
        (acc, x, i) => {
            acc[key(x, i)] = value(x, i);
            return acc;
        },
        {} as Record<K, R>,
    );
}

export function padStart(str: string, targetLength: number, padString: string): string {
    if (str.length >= targetLength) {
        return str;
    }

    const padLength = targetLength - str.length;
    const fullRepeats = Math.floor(padLength / padString.length);
    const remainder = padLength % padString.length;

    return padString.repeat(fullRepeats) + padString.slice(0, remainder) + str;
}

export async function keyByAsync<T, K extends string | number, R>(
    arr: T[],
    key: (x: T) => K,
    value: (x: T) => Promise<R>,
) {
    return (await Promise.all(arr.map(async x => ({ key: key(x), value: await value(x) })))).reduce(
        (acc, x) => {
            acc[x.key] = x.value;
            return acc;
        },
        {} as Record<K, R>,
    );
}

export function mapBy<T, K extends string | number, R = T>(
    arr: readonly T[],
    key: (x: T, i: number) => K,
    value: (x: T, i: number) => R = (x: T) => x as unknown as R,
): Map<K, R> {
    return new Map(arr.map((x, i) => [key(x, i), value(x, i)]));
}

export function multimapBy<T, K extends string | number, R = T>(
    arr: readonly T[],
    key: (x: T, i: number) => K,
    value: (x: T, i: number) => R = (x: T) => x as unknown as R,
): Multimap<K, R> {
    return arr.reduce((acc, x, i) => {
        acc.setOrPush(key(x, i), value(x, i));
        return acc;
    }, new Multimap<K, R>());
}

export function groupBy<T, K extends string | number, R = T>(
    arr: T[],
    key: (x: T) => K,
    value: (x: T) => R = (x: T) => x as unknown as R,
) {
    return arr.reduce(
        (acc, x) => {
            const k = key(x);
            acc[k] = (acc[k] as R[] | undefined) ?? [];
            acc[k].push(value(x));
            return acc;
        },
        {} as Record<K, R[]>,
    );
}

export type Discriminated<Union extends object, Field extends keyof Union, Value extends Union[Field]> = Extract<
    Union,
    { [P in Field]: Value }
>;

export function discriminateOrNull<Union extends object, Field extends keyof Union, Value extends Union[Field]>(
    union: Union,
    field: Field,
    value: Value extends Union[Field] ? Value : never,
): Discriminated<Union, Field, Value> | null {
    return union[field] === value ? (union as Discriminated<Union, Field, Value>) : null;
}

export function discriminate<Union extends object, Field extends keyof Union, Value extends Union[Field]>(
    union: Union,
    field: Field,
    value: Value extends Union[Field] ? Value : never,
): asserts union is Discriminated<Union, Field, Value> {
    if (union[field] !== value) {
        throw new Error(
            `Expected field ${field as string} is "${value as string}" but it was "${union[field] as string}"`,
        );
    }
}

export function pick<T extends Record<string, unknown>, K extends (keyof T)[]>(obj: T, ...keys: K) {
    return keys.reduce(
        (acc, k) => {
            acc[k] = obj[k];
            return acc;
        },
        {} as Pick<T, K[number]>,
    );
}

export function ensureEndsWith(s: string, suffix: string) {
    return s.endsWith(suffix) ? s : `${s}${suffix}`;
}

export function ensureStartsWith(s: string, prefix: string) {
    return s.startsWith(prefix) ? s : `${prefix}${s}`;
}

export function mean(ns: number[]) {
    return ns.reduce((s, n) => s + n, 0) / ns.length;
}

export function partition<T, T1 extends T>(arr: T[], fn: (x: T) => x is T1): [T1[], Exclude<T, T1>[]];
export function partition<T>(arr: T[], fn: (x: T) => boolean): [T[], T[]];
export function partition<T, T1 extends T>(
    arr: Array<T>,
    fn: ((x: T) => x is T1) | ((x: T) => boolean),
): [T1[], Exclude<T, T1>[]] | [T[], T[]] {
    const a = [];
    const b = [];
    for (const x of arr) {
        if (fn(x)) {
            a.push(x);
        } else {
            b.push(x);
        }
    }
    return [a, b as Exclude<T, T1>[]];
}

export function round(n: number, precision: number = 0) {
    if (precision && isFinite(n)) {
        let pair = n.toExponential().split("e");
        const value = Math.round(`${pair[0]}e${Number(pair[1]) + precision}` as unknown as number);

        pair = value.toExponential().split("e");
        return Number(`${pair[0]}e${Number(pair[1]) - precision}`);
    }
    return Math.round(n);
}

export function serialize(value: Serializable, space?: string | number): string {
    return JSON.stringify(
        value,
        (_, v: unknown) => {
            if (typeof v === "bigint") {
                throw new Error("Serialization error: use BigNumber instead of bigint");
            }
            return v;
        },
        space,
    );
}

export function deserialize(value: string) {
    return SerializableSchema.parse(JSON.parse(value));
}

export function cloneData<T extends Serializable>(data: T): T {
    return deserialize(serialize(data)) as T;
}

export function mapValues<K extends string | number | symbol, V, R>(
    x: Record<K, V>,
    fn: (k: K, y: V, i: number) => R,
): Record<K, R>;
export function mapValues<K extends string | number | symbol, V, R>(
    x: Partial<Record<K, V>>,
    fn: (k: K, y: V, i: number) => R,
): Partial<Record<K, R>>;
export function mapValues<K extends string | number | symbol, V, R>(x: Record<K, V>, fn: (k: K, y: V, i: number) => R) {
    return keyBy(
        Object.entries(x).map(([k, v], i) => ({ k, r: fn(k as K, v as V, i) })),
        x => x.k,
        x => x.r,
    );
}

export async function mapValuesAsync<K extends string | number, V, R>(
    x: Record<K, V>,
    fn: (k: K, y: V, i: number) => Promise<R>,
): Promise<Record<K, R>> {
    return await Promise.all(
        Object.entries(x).map(async ([k, v], i) => ({ k: k as K, r: await fn(k as K, v as V, i) })),
    ).then(x =>
        keyBy(
            x,
            y => y.k,
            y => y.r,
        ),
    );
}

export function mapKeys<K extends string | number, V, R extends string | number>(
    x: Record<K, V>,
    fn: (k: K, y: V, i: number) => R,
) {
    return keyBy(
        Object.entries(x).map(([k, v], i) => ({ r: fn(k as K, v as V, i), v: v as V })),
        x => x.r,
        x => x.v,
    );
}

export function mapEntries<K extends string | number, V, K2 extends string | number, V2>(
    x: Record<K, V>,
    fn: (k: K, v: V, i: number) => [K2, V2],
) {
    return Object.entries(x).reduce(
        (acc, [k, v], i) => {
            const [k2, v2] = fn(k as K, v as V, i);
            acc[k2] = v2;
            return acc;
        },
        {} as Record<K2, V2>,
    );
}

export function isFilled(array: unknown[], targetLength: number) {
    return array.length === targetLength && !array.includes(undefined);
}

export function uniq<T>(array: T[]): T[] {
    return Array.from(new Set(array));
}

export function uniqBy<T>(array: T[], fn: (x: T) => unknown): T[] {
    const set = new Set<unknown>();
    return array.reduce<T[]>((acc, x) => {
        const v = fn(x);
        if (!set.has(v)) {
            acc.push(x);
            set.add(v);
        }
        return acc;
    }, []);
}

export type Serializable =
    | string
    | number
    | boolean
    | null
    | BigNumber
    | Serializable[]
    | {
          [key: string]: Serializable;
      };

type _SerializableSchema = z.ZodMiniUnion<
    [
        z.ZodMiniString,
        z.ZodMiniNumber,
        z.ZodMiniBoolean,
        z.ZodMiniNull,
        typeof BigNumberSchema,
        z.ZodMiniArray<SerializableSchema>,
        z.ZodMiniRecord<z.ZodMiniString<string>, SerializableSchema>,
    ]
>;
type _SerializableSchemaInternals = _SerializableSchema["_zod"];

interface SerializableSchemaInternals extends _SerializableSchemaInternals {
    output: Serializable;
    input: Serializable;
}

interface SerializableSchema extends _SerializableSchema {
    _zod: SerializableSchemaInternals;
}

export const SerializableSchema = z
    .lazy(() => {
        return z.union([
            z.string(),
            z.number(),
            z.boolean(),
            z.null(),
            BigNumberSchema,
            z.array(SerializableSchema),
            z.record(z.string(), SerializableSchema),
        ]);
    })
    .register(z.globalRegistry, { title: "Serializable" }) as unknown as SerializableSchema;

export const SerializableObjectSchema = z
    .lazy(() => z.record(z.string(), SerializableSchema))
    .register(z.globalRegistry, { title: "SerializableObject" }) as z.ZodMiniType<{
    [k: string]: Serializable;
}>;
export type SerializableObject = z.infer<typeof SerializableObjectSchema>;

export type Serializify<T> = unknown extends T
    ? never
    : {
          [P in keyof T]: T[P] extends Serializable
              ? T[P]
              : T[P] extends bigint | symbol | Function
                ? never
                : Serializify<T[P]>;
      };

export function range(length: number): Generator<number, void, unknown>;
export function range(from: number, to: number): Generator<number, void, unknown>;
export function* range(lengthOrFrom: number, to?: number): Generator<number, void, unknown> {
    if (to === undefined) {
        for (let i = 0; i < lengthOrFrom; i++) {
            yield i;
        }
    } else if (lengthOrFrom < to) {
        for (let i = lengthOrFrom; i <= to; i++) {
            yield i;
        }
    } else {
        for (let i = lengthOrFrom; i >= to; i--) {
            yield i;
        }
    }
}

export function omit<T extends object, K extends (keyof T)[]>(obj: T, ...keys: K) {
    return Object.entries(obj).reduce(
        (acc, [k, v]) => {
            if (!keys.includes(k as keyof T)) {
                acc[k as Exclude<keyof T, K[number]>] = v;
            }
            return acc;
        },
        {} as Omit<T, K[number]>,
    );
}

export function omitUndefined<T extends object>(obj: T) {
    return Object.fromEntries(Object.entries(obj).filter(([, v]) => typeof v !== "undefined")) as {
        [K in keyof T]-?: Exclude<T[K], undefined>;
    };
}

export function omitUndefinedAndNull<T extends object>(obj: T) {
    return Object.fromEntries(Object.entries(obj).filter(([, v]) => typeof v !== "undefined" && v !== null)) as {
        [K in keyof T]-?: Exclude<T[K], undefined | null>;
    };
}

export function formatError(x: unknown): string {
    if (typeof x === "string") {
        return x;
    }
    if (x instanceof Error) {
        if (x.name === "AggregateError" && "errors" in x && Array.isArray(x.errors)) {
            return `AggregateError:\n${x.errors.map(formatError).join("\n")}`;
        }
        return `${x}\n${x.stack}\nCause:\n${formatError(x.cause)}`;
    }

    return JSON.stringify({ error: x });
}

export function reformatDate(date: string, from: string, to: string) {
    return formatDate(parseDate(date, from, new Date()), to);
}

export function isPDF(f: { contentType: string; filename?: string }) {
    const ct = f.contentType.toLowerCase().trim();
    const fn = f.filename?.toLowerCase().trim() ?? "";

    if (ct === "application/pdf") {
        return true;
    }

    const ambiguous = ["application/octet-stream", "application/others"];
    return ambiguous.includes(ct) && fn.endsWith(".pdf");
}

export function* generateDateRange(from: string | Date, to: string | Date) {
    const startDate = typeof from === "string" ? parseISO(from) : from;
    const endDate = typeof to === "string" ? parseISO(to) : to;
    let currentDate = startDate;

    while (isBefore(currentDate, endDate) || isEqual(currentDate, endDate)) {
        const date = formatDate(currentDate, "yyyy-MM-dd");

        yield { value: currentDate, range: [date] };
        currentDate = addDays(currentDate, 1);
    }
}

export function* generateWeekRange(firstInclude: string | Date, lastInclude: string | Date) {
    const firstIncludeDate = typeof firstInclude === "string" ? parseISO(firstInclude) : firstInclude;
    const lastIncludeDate = typeof lastInclude === "string" ? parseISO(lastInclude) : lastInclude;

    let currentDate = firstIncludeDate;

    while (isBefore(currentDate, lastIncludeDate) || isEqual(currentDate, lastIncludeDate)) {
        const sunday = lastDayOfISOWeek(currentDate);
        const monday = subDays(sunday, 6);
        const range = Array.from({ length: 7 }, (_, i) => {
            return formatDate(addDays(monday, i), "yyyy-MM-dd");
        });

        yield { value: monday, range };
        currentDate = addDays(sunday, 1);
    }
}

export function* generateMonthRange(firstInclude: string | Date, lastInclude: string | Date) {
    const firstIncludeDate = typeof firstInclude === "string" ? parseISO(firstInclude) : firstInclude;
    const lastIncludeDate = typeof lastInclude === "string" ? parseISO(lastInclude) : lastInclude;

    let currentDate = firstIncludeDate;

    while (isBefore(currentDate, lastIncludeDate) || isEqual(currentDate, lastIncludeDate)) {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();

        const range = Array.from({ length: daysInMonth }, (_, i) => {
            return formatDate(addDays(firstDay, i), "yyyy-MM-dd");
        });

        yield { value: firstDay, range };
        currentDate = new Date(year, month + 1, 1);
    }
}

export function getMonday(date: string | Date) {
    return moveToDay(date, "MON");
}

export type ISOWeekDay = "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT" | "SUN";

export function moveToDay(date: string | Date, day: ISOWeekDay, options?: { addWeeks?: number }) {
    const sunday = addWeeks(lastDayOfISOWeek(date), options?.addWeeks ?? 0);
    const sub = {
        SUN: 0,
        SAT: 1,
        FRI: 2,
        THU: 3,
        WED: 4,
        TUE: 5,
        MON: 6,
    }[day];
    const moved = subDays(sunday, sub);
    return formatDate(moved, "yyyy-MM-dd");
}

export function resolveISOWeekDate(year: number, weekNumber: number, weekDay: ISOWeekDay) {
    // https://en.wikipedia.org/wiki/ISO_week_date#:~:text=Weeks%20start%20with%20Monday%20and,days%20close%20to%201%20January.
    return moveToDay(`${year}-01-04`, weekDay, { addWeeks: weekNumber - 1 });
}

export function ensureBusinessDay(date: Date | string) {
    const d = typeof date === "string" ? parseISO(date) : date;
    const day = d.getDay();
    if (day === 6) {
        return addDays(d, 2);
    }
    if (day === 0) {
        return addDays(d, 1);
    }
    return d;
}

export function addBusinessDays(date: Date | string, amount: number) {
    const d = typeof date === "string" ? parseISO(date) : date;
    return addBusinessDaysOriginal(d, amount);
}

export function isAbortError(e: unknown): e is DOMException {
    return e instanceof DOMException && e.name === "AbortError";
}

export class Multimap<K, V> extends Map<K, V[]> {
    setOrPush(k: K, ...v: V[]): this {
        const arr = this.get(k);
        if (arr) {
            arr.push(...v);
        } else {
            this.set(k, v);
        }
        return this;
    }
}

export function sumBy<T>(arr: T[], fn: (x: T) => number) {
    return arr.reduce((acc, x) => acc + fn(x), 0);
}

export function sumByBigNumber<T>(arr: T[], fn: (x: T) => BigNumber) {
    return arr.reduce((acc, x) => fn(x).plus(acc), BigNumber(0));
}

export function findOrDie<T, V>(arr: T[], fn: (x: T) => V, value: V): T;
export function findOrDie<T>(arr: T[], fn: (x: T) => boolean): T;
export function findOrDie(arr: unknown[], fn: (x: unknown) => unknown, value?: unknown) {
    return definedOrDie(value === undefined ? arr.find(fn) : arr.find(x => fn(x) === value), String(value));
}

const camelToSnakeRegex = /([A-Z]+)/g;

export function camelToSnake(x: string) {
    return x.replace(camelToSnakeRegex, x => `_${x.toLowerCase()}`);
}

const snakeToCamelRegex = /(_.)/g;

export function snakeToCamel(x: string) {
    return x.replace(snakeToCamelRegex, x => indexOrDie(x, 1).toUpperCase());
}

export async function parallelize<I, R>(args: {
    items: I[];
    fn: (item: I, index: number, signal?: AbortSignal) => Promise<R>;
    maxParallel: number;
    signal?: AbortSignal;
}) {
    if (args.items.length === 0) {
        return [];
    }

    let max = Math.floor(args.maxParallel);
    if (!Number.isFinite(max) || max < 1) throw new Error("maxParallel must be >= 1");
    max = Math.min(max, args.items.length);
    const results = new Array<R>(args.items.length);
    let i = 0;
    let error: unknown = undefined;
    const controller = !args.signal ? new AbortController() : null;
    const outSignal = args.signal ?? controller?.signal;

    function getNextIndex() {
        return i < args.items.length ? i++ : -1;
    }

    async function worker() {
        while (true) {
            if (error || outSignal?.aborted) {
                return;
            }

            const idx = getNextIndex();
            if (idx === -1) {
                return;
            }

            const item = indexOrDie(args.items, idx);
            try {
                const result = await args.fn(item, idx, outSignal);
                results[idx] = result;
            } catch (e) {
                error = e;
                if (controller) {
                    controller.abort();
                }
                return;
            }
        }
    }

    const workers = Array.from({ length: max }, worker);
    await Promise.allSettled(workers);
    if (error) {
        throw error;
    }

    return results;
}

// discriminatedUnion doesn't handle recursion in TS for now
export const ConditionSchema = z.union([
    z.object({
        op: z.enum(["and", "or"]),
        get value() {
            return z.array(ConditionSchema);
        },
    }),
    z.object({
        op: z.literal("not"),
        get value() {
            return ConditionSchema;
        },
    }),
    z.object({
        op: z.enum(["anyOf"]),
        field: z.string(),
        value: z.array(z.union([z.number(), z.string()])),
    }),
    z.object({
        op: z.enum(["lt", "gt", "lte", "gte"]),
        field: z.string(),
        value: z.union([z.number(), z.string()]),
    }),
    z.object({
        op: z.literal("contains"),
        field: z.string(),
        value: z.string(),
    }),
    z.object({
        op: z.enum(["ne", "eq"]),
        field: z.string(),
        value: z.union([z.number(), z.string(), z.null()]),
    }),
    z.object({
        op: z.enum(["like", "ilike"]),
        field: z.string(),
        value: z.string(),
    }),
]);

ConditionSchema.register(z.globalRegistry, { title: "ConditionSchema" });

export type Condition = z.infer<typeof ConditionSchema>;

export function keys<T extends object>(obj: T): Array<keyof T> {
    return Object.keys(obj) as Array<keyof T>;
}

export function addToISODate(isoDate: string, duration: Duration) {
    return formatDate(add(parseISO(isoDate), duration), "yyyy-MM-dd");
}

export function jsonCodec<S extends z.core.$ZodType<unknown, Serializable>>(schema: S) {
    return z.codec(z.string(), schema, {
        decode: value => JSON.parse(value) as z.input<S>,
        encode: value => JSON.stringify(value),
    });
}

export function urlQueryCodec<
    S extends z.core.$ZodType<unknown, Record<string, string | string[] | undefined>> = z.ZodMiniRecord<
        z.ZodMiniString<string>,
        z.ZodMiniUnion<[z.ZodMiniString<string>, z.ZodMiniArray<z.ZodMiniString<string>>, z.ZodMiniUndefined]>
    >,
>(schema?: S) {
    schema ??= z.record(z.string(), z.union([z.string(), z.array(z.string()), z.undefined()])) as unknown as S;
    return z.codec(z.instanceof(URLSearchParams), schema, {
        encode(value) {
            const result = new URLSearchParams();
            for (const [k, v] of Object.entries(value)) {
                if (Array.isArray(v)) {
                    for (const x of v) {
                        result.append(k, x);
                    }
                } else if (v !== undefined) {
                    result.append(k, v);
                }
            }
            return result;
        },
        decode(value) {
            return Array.from(value).reduce<Record<string, string | string[]>>((acc, [k, v]) => {
                if (k in acc) {
                    if (Array.isArray(acc[k])) {
                        acc[k].push(v);
                    } else {
                        acc[k] = [acc[k] as string, v];
                    }
                } else {
                    acc[k] = v;
                }
                return acc;
            }, {}) as z.input<S>;
        },
    });
}

export const NumberAsStringSchema = z.codec(z.string(), z.number(), {
    encode(value) {
        return String(value);
    },
    decode(value, ctx) {
        const n = Number(value);
        if (!value.trim() || Number.isNaN(n)) {
            ctx.issues.push({ code: "custom", input: value, message: "Invalid string representation of a number" });
            return z.NEVER;
        }
        return n;
    },
});

export const NumberAsStringOrNullSchema = z.codec(z.nullable(z.string()), z.nullable(z.number()), {
    encode(value) {
        return value === null ? null : String(value);
    },
    decode(value) {
        const n = Number(value);
        if (!value || !value.trim() || Number.isNaN(n)) {
            return null;
        }
        return n;
    },
});

export const BooleanAsStringSchema = z.codec(z.string(), z.boolean(), {
    encode(value) {
        return String(value);
    },
    decode(value, ctx) {
        switch (value) {
            case "true":
                return true;
            case "false":
                return false;
            default:
                ctx.issues.push({
                    code: "invalid_value",
                    input: value,
                    values: ["true", "false"],
                });
                return z.NEVER;
        }
    },
});

export function singleToArrayCodec<S extends z.core.$ZodType>(schema: S) {
    return z.codec(z.union([z.array(schema), schema]), z.array(schema), {
        encode(value) {
            return (value.length === 1 ? firstOrDie(value) : value) as z.output<S>[];
        },
        decode(value) {
            return (Array.isArray(value) ? value : [value]) as z.input<S>[];
        },
    });
}

export const CalendarDateSchema = z.codec(z.string(), z.instanceof(CalendarDate), {
    encode(value) {
        return value.toString();
    },
    decode(value) {
        return parseDate2(value);
    },
});

export function base64urlCodec<S extends z.core.$ZodType<unknown, string>>(schema: S) {
    return z.codec(z.base64url(), schema, {
        encode(value) {
            return z.util.uint8ArrayToBase64url(new TextEncoder().encode(value));
        },
        decode(value) {
            return new TextDecoder().decode(z.util.base64urlToUint8Array(value)) as z.input<S>;
        },
    });
}

export function atLeastOneDefinedProperty<T extends object>(obj: T) {
    return Object.values(obj).some(x => x !== undefined);
}

export function atLeastOneDefinedItem(...args: Array<unknown>) {
    return args.some(x => x !== undefined);
}

export const NULL_UUID = "00000000-0000-0000-0000-000000000000";
